<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import 'leaflet/dist/leaflet.css'
import '@/assets/styles/map/styles.css'

import { LayerLevel } from '@/modules/map/enums/layer-level.enum'

import { useDataStore } from '../../data/stores/data.store'
import { useElectionStore } from '../../election/stores/election-map.store'
import { useVoterStore } from '../../voter/stores/voter.store'
import { useMapUiStore } from '@/modules/map/stores/ui.store'
import { useParliamentaryMapStore } from '../stores/parliamentary-map.store'

import ElectionSummary from '@/modules/election/components/ElectionSummary.vue'

import MapHeader from '../components/MapHeader.vue'
import Legend from '../components/Legend.vue'
import ElectionSummaryPollingStation from '@/modules/election/components/ElectionSummaryPollingStation.vue'
import { useConstituencyVoterStore } from '@/modules/voter/stores/constituency-voter.store'
import type ConstituencyAgeGroupModel from '@/modules/voter/models/constituency-age-group.model'

const mapContainer = ref<HTMLElement | null>(null)
const dataStore = useDataStore()
const electionStore = useElectionStore()
const voterStore = useVoterStore()
const voterStoreC = useConstituencyVoterStore()
const mapUiStore = useMapUiStore()
const parliamentaryStore = useParliamentaryMapStore()

const showRegionNewButton = computed(
  () => parliamentaryStore.currentLevel !== LayerLevel.REGION_SIXTEEN
)
const showRegionOldButton = computed(
  () => parliamentaryStore.currentLevel !== LayerLevel.REGION_TEN
)

const showConstituencyButton = computed(
  () =>
    parliamentaryStore.currentLevel !== LayerLevel.CONSTITUENCY &&
    parliamentaryStore.currentLevel !== LayerLevel.REGION_SIXTEEN_CONSTITUENCY
)

function resetStore() {
  electionStore.resetStore()
  voterStore.resetStore()
  mapUiStore.resetStore()
  parliamentaryStore.resetStore()
}

onMounted(async () => {
  await Promise.all([
    dataStore.getShapeFiles(),
    dataStore.getGroupedData(),
    electionStore.getParliamentaryElections(),
    voterStoreC.getConstituencyVoters('akwatia'),
    voterStoreC.getVoterCounts(),
    // voterStore.getVoters()
  ])
  // await parliamentaryStore.getConstituencyShapeFile()
  if (mapContainer.value) {
    parliamentaryStore.initializeMap(mapContainer.value)
    parliamentaryStore.initializeLayers()
  }
})

const mapHeader = computed(() => {
  const year = electionStore.selectedParliamentaryElection.year
  return `PARLIAMENTARY RESULTS MAP ${year}`
})

const ageGroups = ref(['18_30', '31_40', '41_50', '51_60', '61_100', '101plus'])

// Compute aggregated data with filters applied
const aggregated = computed(() => {
  return voterStoreC.aggregateStations()
})

const ageGroupData = computed(() => {
  return Object.keys(aggregated.value.voters).map(ageGroup => ({
    ageGroup: ageGroup.replace('_', '-'),
    total: aggregated.value.voters[ageGroup as keyof ConstituencyAgeGroupModel],
    male: aggregated.value.votersMale[ageGroup as keyof ConstituencyAgeGroupModel],
    female: aggregated.value.votersFemale[ageGroup as keyof ConstituencyAgeGroupModel]
  }));
});

const handleSearch = (query: string) => {
  parliamentaryStore.searchFeatures(query)
}

const handleSearchSelect = (result: any) => {
  parliamentaryStore.highlightFeature(result.name, result.type)
}

const handleSearchHighlight = (result: any) => {
  parliamentaryStore.highlightFeature(result.name, result.type)
}

const handleSearchClear = () => {
  parliamentaryStore.clearHighlight()
}
</script>

<template>
  <div class="parent">
    <div class="parliamentary-container">
      <MapHeader :title="mapHeader" />
      <div class="parliamentary">

        <TabView class="map-statistics" v-model:activeIndex="parliamentaryStore.activeTabIndex">
          <TabPanel header="Result">
            <div class="map-statistics-container">
              <div class="map-statistics-content">
                <ElectionSummaryPollingStation
                  v-if="parliamentaryStore.selectedConstituencies.length > 0"
                  :year="electionStore.selectedParliamentaryElection.year"
                  :summary="electionStore.parliamentaryElectionPollingStationResult"
                />
                <ElectionSummary
                  v-else
                  :year="electionStore.selectedParliamentaryElection.year"
                  :summary="electionStore.parliamentaryElectionResult"
                />

              </div>
              <div class="map-statistics-legend">
                <Button class="control-button" @click="parliamentaryStore.toggleLegend" text>
                  {{ parliamentaryStore.isLegendVisible ? 'Hide Legend' : 'Show Legend' }}
                </Button>
                <Legend
                  v-if="parliamentaryStore.isLegendVisible"
                  title="Win Margin"
                  :items="parliamentaryStore.legendItems"
                  :horizontal="true"
                />
              </div>
            </div>
          </TabPanel>
          <TabPanel header="Demographics">
            <div class="grid">
              <!-- Summary Cards -->
              <div class="col-12">
                <div class="grid">
                  <div class="col-12 md:col-6 lg:col-3">
                    <Card class="bg-blue-50">
                      <template #title>Total Stations</template>
                      <template #content>
                        <div class="text-4xl font-bold">{{ aggregated.totalStations }}</div>
                      </template>
                    </Card>
                  </div>
                  <div class="col-12 md:col-6 lg:col-3">
                    <Card class="bg-green-50">
                      <template #title>Total Voters</template>
                      <template #content>
                        <div class="text-4xl font-bold">
                          {{ aggregated.totalVoters.toLocaleString() }}
                        </div>
                      </template>
                    </Card>
                  </div>
                  <div class="col-12 md:col-6 lg:col-3">
                    <Card class="bg-cyan-50">
                      <template #title>Male Voters</template>
                      <template #content>
                        <div class="text-4xl font-bold">
                          {{ aggregated.totalVotersMale.toLocaleString() }}
                        </div>
                        <div>
                          {{
                            ((aggregated.totalVotersMale / aggregated.totalVoters) * 100).toFixed(
                              1
                            )
                          }}%
                        </div>
                      </template>
                    </Card>
                  </div>
                  <div class="col-12 md:col-6 lg:col-3">
                    <Card class="bg-pink-50">
                      <template #title>Female Voters</template>
                      <template #content>
                        <div class="text-4xl font-bold">
                          {{ aggregated.totalVotersFemale.toLocaleString() }}
                        </div>
                        <div>
                          {{
                            ((aggregated.totalVotersFemale / aggregated.totalVoters) * 100).toFixed(
                              1
                            )
                          }}%
                        </div>
                      </template>
                    </Card>
                  </div>
                </div>
              </div>
            </div>

            <!-- Age Group Breakdown Table -->
            <div class="col-12">
              <Card>
                <template #title>Detailed Age Group Breakdown</template>
                <template #content>
                  <DataTable :value="ageGroupData" :autoLayout="true">
                    <Column field="ageGroup" header="Age Group"></Column>
                    <Column field="total" header="Total Voters">
                      <template #body="{data}">
                        {{ data.total.toLocaleString() }}
                        ({{ ((data.total / aggregated.totalVoters) * 100).toFixed(1) }}%)
                      </template>
                    </Column>
                    <Column field="male" header="Male Voters">
                      <template #body="{data}">
                        {{ data.male.toLocaleString() }}
                        ({{ ((data.male / aggregated.totalVotersMale) * 100).toFixed(1) }}%)
                      </template>
                    </Column>
                    <Column field="female" header="Female Voters">
                      <template #body="{data}">
                        {{ data.female.toLocaleString() }}
                        ({{ ((data.female / aggregated.totalVotersFemale) * 100).toFixed(1) }}%)
                      </template>
                    </Column>
                  </DataTable>
                </template>
              </Card>
            </div>
          </TabPanel>
        </TabView>
        <div class="map-content">
          <div class="map-controls">
            <div class="map-controls-left">
              <Dropdown
                v-model="electionStore.selectedParliamentaryElection"
                :options="electionStore.parliamentaryElections"
                optionLabel="year"
                placeholder="Select Year"
                checkmark
                :highlightOnSelect="true"
              />

              <MultiSelect
                v-if="parliamentaryStore.currentLevel === LayerLevel.REGION_SIXTEEN_CONSTITUENCY"
                v-model="parliamentaryStore.selectedSixteenRegions"
                :options="dataStore.sixteenRegions"
                filter
                optionLabel="name"
                placeholder="Select Regions"
                :maxSelectedLabels="2"
                class="w-full md:w-20rem"
              >
                <template #footer>
                  <div class="py-2 px-3">
                    <b>{{
                      parliamentaryStore.selectedSixteenRegions
                        ? parliamentaryStore.selectedSixteenRegions.length
                        : 0
                    }}</b>
                    item{{
                      (parliamentaryStore.selectedSixteenRegions
                        ? parliamentaryStore.selectedSixteenRegions.length
                        : 0) > 1
                        ? 's'
                        : ''
                    }}
                    selected.
                  </div>
                </template>
              </MultiSelect>
              <MultiSelect
                v-if="parliamentaryStore.currentLevel === LayerLevel.REGION_TEN_CONSTITUENCY"
                v-model="parliamentaryStore.selectedTenRegions"
                :options="dataStore.tenRegions"
                filter
                optionLabel="name"
                placeholder="Select Regions"
                :maxSelectedLabels="2"
                class="w-full md:w-20rem"
              >
                <template #footer>
                  <div class="py-2 px-3">
                    <b>{{
                      parliamentaryStore.selectedTenRegions
                        ? parliamentaryStore.selectedTenRegions.length
                        : 0
                    }}</b>
                    item{{
                      (parliamentaryStore.selectedTenRegions
                        ? parliamentaryStore.selectedTenRegions.length
                        : 0) > 1
                        ? 's'
                        : ''
                    }}
                    selected.
                  </div>
                </template>
              </MultiSelect>

              <MultiSelect
                v-if="parliamentaryStore.currentLevel === LayerLevel.CONSTITUENCY"
                v-model="parliamentaryStore.selectedConstituencies"
                :options="dataStore.constituencies"
                filter
                optionLabel="name"
                placeholder="Select Constituencies"
                :maxSelectedLabels="2"
                class="w-full md:w-18rem"
              >
                <template #footer>
                  <div class="py-2 px-3">
                    <b>{{
                      parliamentaryStore.selectedConstituencies
                        ? parliamentaryStore.selectedConstituencies.length
                        : 0
                    }}</b>
                    item{{
                      (parliamentaryStore.selectedConstituencies
                        ? parliamentaryStore.selectedConstituencies.length
                        : 0) > 1
                        ? 's'
                        : ''
                    }}
                    selected.
                  </div>
                </template>
              </MultiSelect>
              <MultiSelect
                v-if="parliamentaryStore.currentLevel === LayerLevel.CONSTITUENCY_POLLING_STATION"
                v-model="parliamentaryStore.selectedPollingStations"
                :options="parliamentaryStore.filteredPollingStations"
                filter
                optionLabel="name"
                placeholder="Select Polling Stations"
                :maxSelectedLabels="2"
                class="w-full md:w-18rem"
              >
                <template #footer>
                  <div class="py-2 px-3">
                    <b>{{
                      parliamentaryStore.selectedPollingStations
                        ? parliamentaryStore.selectedPollingStations.length
                        : 0
                    }}</b>
                    item{{
                      (parliamentaryStore.selectedPollingStations
                        ? parliamentaryStore.selectedPollingStations.length
                        : 0) > 1
                        ? 's'
                        : ''
                    }}
                    selected.
                  </div>
                </template>
              </MultiSelect>
              <Button
                class="control-button"
                v-if="parliamentaryStore.currentLevel === LayerLevel.CONSTITUENCY_POLLING_STATION"
                text
                disabled
              >
                {{
                  parliamentaryStore.filteredPollingStations
                    ? parliamentaryStore.filteredPollingStations.length
                    : 0
                }}
                Polling Stations
              </Button>
              <Button
                class="control-button"
                v-if="showRegionNewButton"
                @click="parliamentaryStore.setActiveLayer(LayerLevel.REGION_SIXTEEN)"
                text
              >
                16 Regions
              </Button>
              <Button
                class="control-button"
                v-if="showRegionOldButton"
                @click="parliamentaryStore.setActiveLayer(LayerLevel.REGION_TEN)"
                text
              >
                10 Regions
              </Button>
              <Button
                v-if="showConstituencyButton"
                @click="parliamentaryStore.setActiveLayer(LayerLevel.CONSTITUENCY)"
                text
              >
                Constituencies
              </Button>
              <Button @click="parliamentaryStore.fitBounds" text>Resize Map</Button>
              <Button @click="parliamentaryStore.toggleColorMode" text>Toggle Color</Button>
              <Button @click="resetStore" text>Reset</Button>
            </div>
          </div>
          <div ref="mapContainer" class="map-container"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Container for each child and its heading */
.presidential-container,
.parliamentary-container {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  width: 100%;
}

.map-controls {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 12px;
}

.map-controls-left {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.map-controls-right {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
